export const ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  NOT_FOUND: "NOT_FOUND",
  VALIDATION_FAILED: "VALIDATION_FAILED",
  CONFLICT: "CONFLICT",
  RATE_LIMITED: "RATE_LIMITED",
  DATABASE_ERROR: "DATABASE_ERROR",
  UNKNOWN: "UNKNOWN",
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export class AppError extends Error {
  readonly code: ErrorCode;

  constructor(code: ErrorCode, message: string) {
    super(message);
    this.name = "AppError";
    this.code = code;
  }
}

/**
 * Pluggable monitoring adapter. Defaults to a no-op so `@template/shared`
 * stays dependency-free (no React, no Node-specific SDK imports). Each app
 * installs its own adapter at startup — see:
 *   - apps/api/src/lib/monitoring.ts       (@sentry/node)
 *   - apps/worker/src/lib/monitoring.ts    (@sentry/node)
 *   - apps/web/src/lib/monitoring.ts       (@sentry/react)
 *
 * Works with any Sentry-protocol backend: Sentry, GlitchTip, etc.
 */
type MonitoringAdapter = (error: Error, context?: Record<string, unknown>) => void;

let adapter: MonitoringAdapter = () => {};

export function setMonitoringAdapter(fn: MonitoringAdapter): void {
  adapter = fn;
}

export function reportToMonitoring(error: Error, context?: Record<string, unknown>): void {
  adapter(error, context);
}

/** Structured error report for error boundaries. Logs JSON and reports to the adapter. */
export function reportError(error: Error & { digest?: string }, context: { route: string }): void {
  console.error(
    JSON.stringify({
      type: "ErrorBoundary",
      route: context.route,
      name: error.name,
      message: error.message,
      digest: error.digest,
      timestamp: new Date().toISOString(),
    }),
  );
  reportToMonitoring(error, { type: "ErrorBoundary", route: context.route, digest: error.digest });
}
